import { Link } from "react-router-dom";
import { CONTACT_ROUTE, STORE_LEGAL_NAME } from "@/utils/constants.js";
import { formatAddressSummary } from "@/utils/storeAddress.js";
import { useStoreInfo } from "@/store/StoreInfoContext.jsx";
import "./legal.css";

export default function TermsPage() {
    const { storeInfo } = useStoreInfo();

    const email = storeInfo?.legalEmail?.trim() || "";
    const legalAddress = formatAddressSummary(storeInfo?.legalAddress);
    const phoneNumber = storeInfo?.legalPhone?.trim() || "";
    const legalName = storeInfo?.legalName?.trim() || STORE_LEGAL_NAME;

    return (
        <main className="container legal-page">
            <h1>Mentions légales et conditions générales</h1>
            <p className="legal-intro muted">
                Les présentes informations régissent l&apos;utilisation de la plateforme en ligne de {legalName}.
            </p>

            <section className="card legal-section">
                <h2>Mentions légales</h2>
                <dl className="legal-details">
                    <div>
                        <dt>Éditeur du site</dt>
                        <dd>{legalName}</dd>
                    </div>
                    {legalAddress && (
                        <div>
                            <dt>Adresse légale</dt>
                            <dd>{legalAddress}</dd>
                        </div>
                    )}
                    {email && (
                        <div>
                            <dt>E-mail</dt>
                            <dd><a href={`mailto:${email}`}>{email}</a></dd>
                        </div>
                    )}
                    {phoneNumber && (
                        <div>
                            <dt>Téléphone</dt>
                            <dd>
                                <a href={`tel:${phoneNumber.replace(/\s/g, "")}`}>{phoneNumber}</a>
                            </dd>
                        </div>
                    )}
                    <div>
                        <dt>Directeur de la publication</dt>
                        <dd>Le responsable de {legalName}</dd>
                    </div>
                    <div>
                        <dt>Hébergement</dt>
                        <dd>
                            Les services techniques de la plateforme sont fournis par l&apos;infrastructure
                            d&apos;hébergement configurée pour le déploiement du catalogue en ligne.
                        </dd>
                    </div>
                </dl>
            </section>

            <section className="card legal-section">
                <h2>Conditions générales d&apos;utilisation (CGU)</h2>
                <div className="legal-prose">
                    <p>
                        L&apos;accès et l&apos;utilisation du site impliquent l&apos;acceptation sans réserve
                        des présentes conditions générales d&apos;utilisation.
                    </p>
                    <h3>Objet</h3>
                    <p>
                        Le site permet de consulter le catalogue de produits, de créer un compte client,
                        de gérer un panier et de passer commande auprès de {legalName}.
                    </p>
                    <h3>Compte utilisateur</h3>
                    <p>
                        L&apos;utilisateur s&apos;engage à fournir des informations exactes lors de la création
                        de son compte et à préserver la confidentialité de ses identifiants. Toute activité
                        réalisée depuis le compte est réputée effectuée par son titulaire.
                    </p>
                    <h3>Propriété intellectuelle</h3>
                    <p>
                        Les contenus du site (textes, visuels, structure, marques et logos) sont protégés.
                        Toute reproduction ou exploitation non autorisée est interdite.
                    </p>
                    <h3>Responsabilité</h3>
                    <p>
                        {legalName} s&apos;efforce d&apos;assurer l&apos;exactitude des informations publiées,
                        sans garantir l&apos;absence d&apos;erreur ou d&apos;interruption de service. L&apos;éditeur
                        ne saurait être tenu responsable d&apos;un usage non conforme du site par l&apos;utilisateur.
                    </p>
                </div>
            </section>

            <section className="card legal-section">
                <h2>Conditions générales de vente (CGV)</h2>
                <div className="legal-prose">
                    <p>
                        Toute commande passée sur la plateforme vaut acceptation des présentes conditions
                        générales de vente.
                    </p>
                    <h3>Produits et prix</h3>
                    <p>
                        Les produits sont présentés avec leurs caractéristiques essentielles et leurs prix en FCFA.
                        Les prix applicables sont ceux affichés au moment de la validation de la commande,
                        sous réserve de disponibilité en stock.
                    </p>
                    <h3>Commande</h3>
                    <p>
                        La validation d&apos;une commande constitue un engagement ferme de l&apos;acheteur.
                         {legalName} se réserve le droit de refuser ou d&apos;annuler une commande en cas
                        d&apos;anomalie, de litige antérieur ou de stock indisponible.
                    </p>
                    <h3>Livraison et retrait</h3>
                    <p>
                        Le client choisit, lors du passage de commande, entre le retrait en magasin et la livraison
                        lorsque cette option est proposée. Les modalités pratiques sont confirmées lors du traitement
                        de la commande.
                    </p>
                    <p>
                        * Livraison gratuite jusqu&apos;à 10&nbsp;km à partir de nos magasins, selon disponibilité
                        et zone desservie.
                    </p>
                    <h3>Paiement</h3>
                    <p>
                        Les modalités de paiement acceptées sont communiquées par {legalName} lors du traitement
                        de la commande ou à la prise de contact avec le client.
                    </p>
                    <h3>Droit de rétractation et retours</h3>
                    <p>
                        Les conditions de retour, d&apos;échange ou d&apos;annulation sont définies conformément
                        à la réglementation applicable et aux règles commerciales de {legalName}. Pour toute demande,
                        le client peut contacter le magasin via la page{" "}
                        <Link to={CONTACT_ROUTE}>Contact</Link>.
                    </p>
                    <h3>Données personnelles</h3>
                    <p>
                        Les données collectées sont nécessaires à la gestion du compte client, du panier et des
                        commandes. Elles ne sont utilisées que dans ce cadre et ne sont pas cédées à des tiers
                        à des fins commerciales sans consentement.
                    </p>
                    <h3>Litiges</h3>
                    <p>
                        En cas de difficulté, le client est invité à contacter {legalName} en priorité afin de
                        rechercher une solution amiable. À défaut, les tribunaux compétents du ressort du siège
                        de l&apos;éditeur pourront être saisis, sous réserve des dispositions légales impératives.
                    </p>
                </div>
            </section>

            <p className="legal-updated muted">
                Dernière mise à jour : {new Date().getFullYear()}
            </p>
        </main>
    );
}
